import { MotionConfig, fadeUp, motion } from '@/lib/motion';
import {
  ArrowRight,
  Clock,
  FileText,
  MessageSquare,
  PhoneForwarded,
  Radio,
  Sparkles,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { useBrand } from '@/brand/brand-provider';
import { AgentRolesSection } from '@/components/marketing/agent-roles-section';
import { AgentSetupWalkthrough } from '@/components/marketing/agent-setup-walkthrough';
import { DoneForYouSection } from '@/components/marketing/done-for-you-section';
import { HeroCallCard } from '@/components/marketing/hero-call-card';
import { Button } from '@/components/ui/button';
import { Seo } from '@/lib/seo';

const FEATURES = [
  {
    icon: Radio,
    title: 'Natural, real-time conversation',
    desc: 'Barge-in handling lets callers interrupt naturally, and sub-second neural TTS keeps the back-and-forth flowing like a real receptionist.',
  },
  {
    icon: Clock,
    title: 'Sub-second responses',
    desc: 'Typical first response around 380ms. No awkward dead air — the agent feels present and attentive on every call.',
  },
  {
    icon: PhoneForwarded,
    title: 'Transfer to a human',
    desc: 'Callers who ask for a person are forwarded straight to your own number. Define rules in plain language — urgent or out-of-scope calls reach the right person in seconds.',
  },
  {
    icon: FileText,
    title: 'Captures booking requests',
    desc: 'Names, numbers, preferred times — collected on the call and dropped into your dashboard with an AI summary.',
  },
  {
    icon: Sparkles,
    title: 'Answers from your knowledge base',
    desc: 'Hours, pricing, policies — answered straight from your uploaded docs. No made-up answers, only your content.',
  },
  {
    icon: MessageSquare,
    title: 'Same brain as your chat widget',
    desc: 'Train once. The phone agent and the website chat share one knowledge base, so answers never drift between channels.',
  },
];

const FAQS = [
  {
    q: 'Does the voice agent really sound human?',
    a: 'Yes — low-latency neural voices stream with sub-second response times, so conversations flow naturally. You can tune voice, speed, and personality per agent.',
  },
  {
    q: 'Can it transfer calls to my team?',
    a: 'Yes. If a caller wants a human, the agent forwards the call to your number. Define transfer rules in plain language — e.g. “emergencies go to the on-call mobile” — and the agent connects callers in seconds while keeping the transcript. Every forward number is verified first (we call it with a code), so calls can only be forwarded to numbers you control.',
  },
  {
    q: 'Which countries can I get a number in?',
    a: 'Local numbers are available in the US, UK, Australia, and New Zealand.',
  },
  {
    q: 'What happens after hours?',
    a: 'The agent answers 24/7. It takes messages, captures booking requests, and flags anything urgent for callback — nothing is lost to voicemail.',
  },
];

export function VoicePage(): JSX.Element {
  const brand = useBrand();
  return (
    <MotionConfig reducedMotion="user">
      <Seo
        title={`AI Voice Agent — ${brand.displayName}`}
        description="An AI phone receptionist that answers every call 24/7, holds natural sub-second conversations, captures booking requests, and transfers on intent — all from one knowledge base shared with your website chat."
        path="/voice"
        image="/og-voice.png"
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-0 h-[500px] w-[900px] -translate-x-1/2 -translate-y-1/3 rounded-full bg-gradient-to-br from-[var(--brand-accent)]/20 via-[var(--brand-accent)]/5 to-transparent blur-3xl" />
        </div>
        <div className="container grid grid-cols-1 items-center gap-12 py-14 sm:py-20 lg:grid-cols-2">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-accent)]/10 px-3 py-1 text-xs font-semibold text-[var(--brand-accent)]">
              AI Voice Agent
            </span>
            <h1 className="mt-5 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              Answers every phone call, 24/7.
            </h1>
            <p className="mt-5 max-w-xl text-lg text-muted-foreground">
              A natural-sounding AI receptionist that picks up on the first ring,
              books and transfers in real time, and answers from the same knowledge
              base as your website chat.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg">
                <Link to="/signup">
                  Get started <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/pricing">See pricing</Link>
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <HeroCallCard />
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="border-y border-border bg-muted/20 py-14 sm:py-20">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Everything a great receptionist does — without the desk.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ delay: (i % 3) * 0.1 }}
                className="rounded-2xl border border-border bg-card p-6"
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--brand-accent)]/10 text-[var(--brand-accent)]">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-sell to chat */}
      <section className="container py-14 sm:py-20">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-6 rounded-3xl border border-border bg-gradient-to-br from-emerald-500/5 to-transparent p-10 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
            <MessageSquare className="h-6 w-6" />
          </span>
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Same brain on your website
          </h2>
          <p className="max-w-xl text-muted-foreground">
            Every plan also includes the embeddable chat widget — answering website
            visitors from the very same knowledge base. Train once, answer everywhere.
          </p>
          <Button asChild variant="outline">
            <Link to="/chat">
              Explore the chat widget <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border bg-muted/20 py-14 sm:py-20">
        <div className="container mx-auto max-w-2xl">
          <h2 className="text-center text-3xl font-bold tracking-tight sm:text-4xl">
            Voice agent FAQ
          </h2>
          <div className="mt-10 space-y-4">
            {FAQS.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-medium">{f.q}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deep product detail relocated from the landing page: what roles the
          agent can play, how setup works, and the done-for-you option. */}
      <AgentRolesSection />
      <AgentSetupWalkthrough />
      <DoneForYouSection />

      {/* CTA */}
      <section className="container py-14 sm:py-20">
        <div className="rounded-3xl border border-border bg-gradient-to-br from-[var(--brand-accent)] to-fuchsia-600 px-6 py-14 text-center text-white sm:px-12">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Never miss a call again.
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-white/80">
            From $39/month — voice and chat included on one knowledge base.
          </p>
          <div className="mt-8">
            <Button
              asChild
              size="lg"
              className="bg-white text-[var(--brand-accent)] hover:bg-white/90"
            >
              <Link to="/signup">
                Get started <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </MotionConfig>
  );
}
